import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent } from '../lib/firestore/events';
import { getEventCohosts, addEventCohost, removeEventCohost } from '../lib/firestore/eventCohosts';
import { getGroupMembers } from '../lib/firestore/groups';
import '../styles/EventCohosts.css';

const EventCohosts = () => {
  const { eventId } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [cohosts, setCohosts] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMember, setSelectedMember] = useState('');

  useEffect(() => {
    if (eventId && user) {
      loadData();
    } else if (!user) {
      navigate('/login');
    }
  }, [eventId, user, navigate]);

  const loadData = async () => {
    if (!eventId || !user) return;

    setLoading(true);
    try {
      const eventData = await getEvent(eventId);
      if (!eventData) {
        setError('Event not found');
        setLoading(false);
        return;
      }

      // Check if user is host
      if (eventData.hostId !== user.uid) {
        setError('Only the event host can manage cohosts');
        setLoading(false);
        return;
      }

      setEvent(eventData);

      // Load cohosts
      const cohostsList = await getEventCohosts(eventId);
      setCohosts(cohostsList);

      // If event is under a group, load group members
      if (eventData.hostType === 'group') {
        const members = await getGroupMembers(eventData.hostId);
        // Filter out already cohosts and the host
        const available = members.filter(
          (m) =>
            m.userId !== user.uid &&
            !cohostsList.some((c) => c.userId === m.userId)
        );
        setAvailableMembers(available);
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCohost = async () => {
    if (!selectedMember || !user || !eventId) return;

    try {
      await addEventCohost(eventId, selectedMember, user.uid);
      await loadData();
      setSelectedMember('');
    } catch (err) {
      setError(err.message || 'Failed to add cohost');
    }
  };

  const handleRemoveCohost = async (userId) => {
    if (!confirm('Remove this cohost?')) return;

    try {
      await removeEventCohost(eventId, userId);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove cohost');
    }
  };

  if (loading) {
    return (
      <div className="event-cohosts-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-cohosts-container">
        <div className="error-message">{error || 'Event not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate(`/events/${eventId}`)}>
          Back to Event
        </button>
      </div>
    );
  }

  return (
    <div className="event-cohosts-container">
      <div className="event-cohosts-card">
        <header className="cohosts-header">
          <button className="btn-back" onClick={() => navigate(`/events/${eventId}`)}>
            ← Back to Event
          </button>
          <h1>Manage Cohosts</h1>
          <p>{event.title}</p>
        </header>

        <div className="cohosts-content">
          {/* Add Cohost Section */}
          {event.hostType === 'group' && availableMembers.length > 0 && (
            <section className="add-cohost-section">
              <h2>Add Cohost</h2>
              <div className="add-cohost-form">
                <select
                  className="input"
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                >
                  <option value="">Select a member...</option>
                  {availableMembers.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      User {member.userId.slice(0, 8)}... ({member.role})
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-primary"
                  onClick={handleAddCohost}
                  disabled={!selectedMember}
                >
                  Add as Cohost
                </button>
              </div>
            </section>
          )}

          {event.hostType === 'profile' && (
            <p className="info-message">
              Profile-level events don't support cohosts. Create the event in a group to enable cohosting.
            </p>
          )}

          {/* Current Cohosts */}
          <section className="cohosts-list-section">
            <h2>Current Cohosts ({cohosts.length})</h2>
            {cohosts.length === 0 ? (
              <p className="empty-state">No cohosts added yet</p>
            ) : (
              <div className="cohosts-list">
                {cohosts.map((cohost) => (
                  <div key={cohost.userId} className="cohost-row">
                    <div className="cohost-info">
                      <div className="cohost-id">User {cohost.userId.slice(0, 8)}...</div>
                      <div className="cohost-meta">
                        Added {new Date(cohost.addedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleRemoveCohost(cohost.userId)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default EventCohosts;

